import type { Database, DatabaseEntry, DatabaseField, DatabaseTemplate } from '~/api-client';
import { isVisible } from '~/modules/common/services/util/visibilityHelpers';

/**
 * Reine (Vue-unabhaengige) Logik der Grid-/Galerie-Vorschaufelder — zentral,
 * damit DbEditorV2 (Editor), DbGridEntryView und DbGalleryEntryView dieselbe
 * Wahrheit nutzen und die Regeln per Vitest testbar sind.
 *
 * Drei Zustaende je Feld und Ansicht:
 *   - „blue"   = immer anzeigen (grid/gallery an, NICHT conditional; oder Legacy
 *                displayInPreview = alle Ansichten).
 *   - „orange" = nur anzeigen, wenn der Anzeigefilter der FELDGRUPPE des Felds
 *                fuer den Datensatz passt (wirkt im Render nur bei
 *                appearance.respectFieldGroupsInPreview = true).
 *   - „off"    = nicht anzeigen.
 * Invariante beim Auswaehlen: „blau + orange je Feldgruppe ≤ PREVIEW_MAX_FIELDS"
 * (nie mehr als so viele Felder gleichzeitig sichtbar).
 */
export type PreviewView = 'grid' | 'gallery';
export type PreviewState = 'off' | 'blue' | 'orange';

export const PREVIEW_MAX_FIELDS = 8;
const NO_GROUP = '__none__';

const condKey = (view: PreviewView): 'gridConditional' | 'galleryConditional' =>
  view === 'grid' ? 'gridConditional' : 'galleryConditional';

export function previewState(field: DatabaseField, view: PreviewView): PreviewState {
  // Legacy „alle Ansichten" gewinnt: immer blau.
  if (field.displayInPreview === true) return 'blue';
  const o = field.displayPreviewOptions as Record<string, boolean | undefined> | undefined;
  if (o && o[view] && o[condKey(view)]) return 'orange';
  if (o && o[view]) return 'blue';
  return 'off';
}

/**
 * „Wird dieses Feld in Ansicht X als Vorschaufeld gezeigt?" — EINE Wahrheit
 * fuer alle Ansichten und alle Templates.
 *
 * Vorher war diese Frage in ueber 20 Komponenten inline nachgebaut, in drei
 * verschiedenen Semantiken. Das kostete am 2026-08-13 eine komplett leere
 * Tabellenansicht: der Store (= was gerendert wird) kannte das Legacy-Flag
 * nicht, Spaltenzaehler und Ansichts-Freischaltung schon — Ansicht angeboten,
 * Spalten gemeldet, nichts gerendert.
 *
 * Regel (identisch zu `previewState()` fuer Grid/Galerie): Legacy
 * `displayInPreview = true` bedeutete „alle Ansichten" und gewinnt; sonst
 * entscheidet das Flag der jeweiligen Ansicht. Ein explizites
 * `displayInPreview: false` ist KEIN Veto — der Feld-Editor setzt es genau so,
 * wenn er ein Feld auf das neue Modell umstellt (s. DbEditorV2 `togglePreview`).
 */
export type PreviewViewAll = 'list' | PreviewView;

/** Minimalform: akzeptiert `DatabaseField` genauso wie die schlanken
 *  Feld-Shapes der Resolver (list-fields-resolver `MinimalField`). */
export interface PreviewFlaggedField {
  displayInPreview?: boolean | null;
  displayPreviewOptions?: { list?: boolean; grid?: boolean; gallery?: boolean } | null;
}

export function isPreviewFieldForView(field: PreviewFlaggedField | undefined, view: PreviewViewAll): boolean {
  if (!field) return false;
  if (field.displayInPreview === true) return true;
  return field.displayPreviewOptions?.[view] === true;
}

/** Listenansicht — die mit Abstand haeufigste Frage, daher eigener Name. */
export const isListPreviewField = (field: PreviewFlaggedField | undefined): boolean =>
  isPreviewFieldForView(field, 'list');

/** „Irgendwo in der Vorschau sichtbar" — fuer das Key-Visual der Detailmasken,
 *  das sich das erste Bildfeld aus den Vorschaufeldern greift. */
export const isPreviewFieldAnyView = (field: PreviewFlaggedField | undefined): boolean =>
  isPreviewFieldForView(field, 'list')
  || isPreviewFieldForView(field, 'grid')
  || isPreviewFieldForView(field, 'gallery');

export function countBluePreview(fields: DatabaseField[], view: PreviewView): number {
  return fields.filter((f) => previewState(f, view) === 'blue').length;
}

export function orangeByGroup(fields: DatabaseField[], view: PreviewView): Map<string, number> {
  const m = new Map<string, number>();
  fields.forEach((f) => {
    if (previewState(f, view) === 'orange') {
      const g = f.fieldGroup ?? NO_GROUP;
      m.set(g, (m.get(g) ?? 0) + 1);
    }
  });
  return m;
}

export function maxOrangePreview(fields: DatabaseField[], view: PreviewView): number {
  let mx = 0;
  orangeByGroup(fields, view).forEach((v) => { if (v > mx) mx = v; });
  return mx;
}

export type CycleResult = { action: 'set'; state: PreviewState } | { action: 'blocked'; reason: 'limit' };

/**
 * Naechster Zustand beim Klick auf das Grid-/Galerie-Icon (rein, ohne
 * Seiteneffekt). Zyklus: grau → blau → orange → grau. „blau→orange" braucht eine
 * Feldgruppe (sonst 2-Zustand: zurueck auf grau) und erhoeht die gleichzeitige
 * Sichtbarkeit nie. „grau→blau" ist nur erlaubt, solange die Invariante haelt;
 * ist blau voll, aber orange passt (Feldgruppe + Platz), wird direkt orange.
 */
export function resolvePreviewCycle(
  field: DatabaseField,
  fields: DatabaseField[],
  view: PreviewView,
  max: number = PREVIEW_MAX_FIELDS,
): CycleResult {
  const state = previewState(field, view);
  if (state === 'off') {
    if ((countBluePreview(fields, view) + 1) + maxOrangePreview(fields, view) <= max) {
      return { action: 'set', state: 'blue' };
    }
    // blau voll -> orange nur, wenn das Feld eine Feldgruppe hat und dort noch
    // Platz ist (orange ohne Feldgruppe ist ausgeblendet, s.u.).
    if (field.fieldGroup) {
      const inGroup = orangeByGroup(fields, view).get(field.fieldGroup) ?? 0;
      if (countBluePreview(fields, view) + (inGroup + 1) <= max) {
        return { action: 'set', state: 'orange' };
      }
    }
    return { action: 'blocked', reason: 'limit' };
  }
  if (state === 'blue') {
    // Der orange Zustand ist NUR verfuegbar, wenn das Feld einer Feldgruppe
    // zugeordnet ist (orange = an den Gruppen-Anzeigefilter gekoppelt). Ohne
    // Feldgruppe ist orange ausgeblendet -> 2-Zustand (blau <-> grau).
    if (!field.fieldGroup) return { action: 'set', state: 'off' };
    return { action: 'set', state: 'orange' };
  }
  return { action: 'set', state: 'off' }; // orange -> grau
}

/**
 * Waehlt die pro Datensatz sichtbaren Vorschaufelder einer Karte aus.
 * respect=false → nur blaue (orange ausgeblendet). respect=true → blaue immer +
 * orange, deren Feldgruppe den Anzeigefilter fuer den Datensatz erfuellt (ohne
 * Gruppe nie). Harte Kappung: blau zuerst, dann orange in Feldreihenfolge;
 * Ergebnis in urspruenglicher Feldreihenfolge (stabiles Keyvisual/Titel).
 */
export function selectPreviewFields(
  fields: DatabaseField[],
  entry: DatabaseEntry | undefined,
  database: Database | DatabaseTemplate,
  view: PreviewView,
  respect: boolean,
  max: number = PREVIEW_MAX_FIELDS,
): DatabaseField[] {
  const blue = fields.filter((f) => previewState(f, view) === 'blue');
  if (!respect) return blue.slice(0, max);

  const groupPasses = (f: DatabaseField): boolean => {
    if (!f.fieldGroup) return false;
    const group = (database as Database)?.settings?.fieldGroups?.find((g) => g.id === f.fieldGroup);
    if (!group) return false;
    return isVisible(group, database, entry);
  };
  const orange = fields.filter((f) => previewState(f, view) === 'orange' && groupPasses(f));
  const chosen = new Set<DatabaseField>(blue.slice(0, max));
  for (const f of orange) {
    if (chosen.size >= max) break;
    chosen.add(f);
  }
  return fields.filter((f) => chosen.has(f));
}
